import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Search, Loader2 } from 'lucide-react';
import ticketsService from '@/services/tickets.service';
import { transformApiTicketToFrontend } from '@/lib/ticketUtils';
import { useTranslation } from 'react-i18next';

interface TicketListModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTicket: (ticket: any) => void;
}

export default function TicketListModal({ isOpen, onClose, onSelectTicket }: TicketListModalProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInContent, setSearchInContent] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Debounced search effect - same as header search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    const timer = setTimeout(async () => {
      try {
        const response = await ticketsService.searchTickets({
          q: searchQuery,
          searchInContent: searchInContent,
          limit: 50
        });
        const transformedResults = response.data.tickets.map(transformApiTicketToFrontend);
        setSearchResults(transformedResults);
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, searchInContent]);

  const handleTicketSelect = (ticket: any) => {
    console.log('🎯 TicketListModal - handleTicketSelect called with:', ticket);
    console.log('🎯 TicketListModal - ticket.id:', ticket.id);
    console.log('🎯 TicketListModal - ticket.subject:', ticket.subject);
    console.log('🎯 TicketListModal - ticket.ticketNumber:', ticket.ticketNumber);
    console.log('🎯 TicketListModal - ticket.status:', ticket.status);
    onSelectTicket(ticket);
    setSearchQuery('');
    setSearchResults([]);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-white">
        <DialogHeader>
          <DialogTitle>{t('common:x3.addTicket', 'Ticket hinzufügen')}</DialogTitle>
          <DialogDescription>
            {t('common:x3.searchAndSelectTicket', 'Suchen Sie nach einem Ticket und wählen Sie es aus')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search Input with Toggle */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder={t('common:x1.searchTicketPh', 'Ticket suchen (Nummer, Betreff, E-Mail...)')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                autoFocus
              />
            </div>

            {/* Search Options Toggle */}
            <div className="flex items-center gap-2 whitespace-nowrap">
              <Switch
                id="modal-search-in-content"
                checked={searchInContent}
                onCheckedChange={setSearchInContent}
                className="data-[state=checked]:bg-[#0F99FD]"
              />
              <label
                htmlFor="modal-search-in-content"
                className="text-sm text-gray-700 cursor-pointer"
              >
                {t('common:x1.searchInContent', 'Im Inhalt suchen')}
              </label>
            </div>
          </div>

          {/* Search Results */}
          <div className="min-h-[300px] max-h-[400px] overflow-y-auto border rounded-md">
            {!searchQuery.trim() ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <Search className="h-12 w-12 mb-3" />
                <p className="text-sm">{t('common:x1.enterSearchTerm', 'Geben Sie einen Suchbegriff ein')}</p>
              </div>
            ) : isSearching ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                <span className="ml-2 text-sm text-gray-500">{t('common:x1.searching', 'Suche läuft...')}</span>
              </div>
            ) : searchResults.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <Search className="h-12 w-12 mb-3" />
                <p className="text-sm">{t('common:x1.noTicketsFound', 'Keine Tickets gefunden')}</p>
              </div>
            ) : (
              <div className="divide-y">
                {searchResults.map((ticket) => (
                  <div
                    key={ticket.id}
                    className="cursor-pointer border-b border-slate-200/60 p-3 last:border-b-0 hover:bg-slate-50 transition-colors"
                    onClick={() => handleTicketSelect(ticket)}
                  >
                    <div className="mb-1 flex items-center justify-between">
                      <span className="text-sm font-semibold text-slate-800">
                        {ticket.subject}
                      </span>
                      <span className="text-xs text-slate-500">
                        {ticket.ticketNumber}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <span className="truncate">{ticket.requester?.name || 'Unknown'}</span>
                      <span className="text-slate-300">•</span>
                      <span className="truncate">{ticket.organization?.name || 'N/A'}</span>
                      <span className="text-slate-300">•</span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                          ticket.status === 'open'
                            ? 'bg-green-100 text-green-700'
                            : ticket.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-700'
                            : ticket.status === 'closed'
                            ? 'bg-slate-100 text-slate-600'
                            : 'bg-blue-100 text-blue-700'
                        }`}
                      >
                        {ticket.status === 'open' ? 'Offen' :
                         ticket.status === 'pending' ? 'Wartend' :
                         ticket.status === 'closed' ? 'Geschlossen' : 'Neu'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t('common:action.cancel', 'Abbrechen')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}